/**
 * Safely stringifies an untrusted value for display.
 *
 * Use this anywhere a string is expected at runtime but the source is
 * untyped (LLM/tool-call args, JSON parsed from external systems, etc.) and
 * a non-string would either crash or render as `[object Object]`. Notable
 * crash site: `monaco.editor.createModel`, which throws on non-strings.
 */
export function coerceToString(value: unknown): string {
  if (typeof value === 'string') {
    return value;
  }
  if (value == null) {
    return '';
  }
  if (value instanceof Date) {
    // Invalid dates (e.g. `new Date('garbage')`) throw RangeError on
    // toISOString(). Detect and fall through to the empty-string default
    // so this helper stays crash-proof.
    if (Number.isNaN(value.getTime())) {
      return '';
    }
    return value.toISOString();
  }
  if (typeof value === 'object') {
    try {
      const json = JSON.stringify(value);
      return json ?? '';
      // Intentionally swallow: this helper is a last-line defense before
      // Monaco's createModel, which throws on non-strings. JSON.stringify
      // can throw on circular refs or BigInt-containing objects; rethrowing
      // would reintroduce the exact crash the helper exists to prevent.
      // eslint-disable-next-line benchlint/always-throw-from-catch
    } catch {
      return '[unserializable]';
    }
  }
  return String(value);
}
